import { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LatestContractDtoFragment,
  CareerPathEvaluation,
  CareerPathDto,
  FullMeetingFragment,
  CountrySettingsFragment,
  GovernmentJobTitle,
  GovernmentJobTitleTinyDto,
} from '../../../types'
import { ContractChangeTable } from './ContractChangeTable'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import useEmployeesCurrentContractChanges from '../../../graphql/contract/hooks/useEmployeesCurrentContractChanges'
import { isGovernmentJobTitleValid, transformChangeData, transformFromStringToGovernmentJobTitle } from './helpers'

interface Props {
  meeting: FullMeetingFragment
  currentContract: LatestContractDtoFragment
  careerPathEvaluation: CareerPathEvaluation | null
  careerPathData: CareerPathDto[] | null
  isPreparation?: boolean
  toggleExistingUnknownGovernmentJobTitle?: Dispatch<SetStateAction<boolean>>
  countrySettings: CountrySettingsFragment
  governmentJobTitles?: GovernmentJobTitle[]
}
export const CurrentPendingContractChanges: FC<Props> = ({
  meeting,
  currentContract,
  careerPathEvaluation,
  careerPathData,
  isPreparation = false,
  toggleExistingUnknownGovernmentJobTitle,
  countrySettings,
  governmentJobTitles = undefined,
}) => {
  const [t] = useTranslation()
  const { loading, error, data } = useEmployeesCurrentContractChanges(meeting.employee.id)

  if (loading || error || !data || !data.contractChangesByEmployee) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.contractChangesByEmployee} error={error} />
  }

  if (!currentContract.annualSalary || !currentContract.employeeLevel) {
    return (
      <PerFlexRow justify="space-between" align="middle" mb="xs" mr="s">
        <PerText appearance="heading" mb="xs" color="primary">
          {t(`appraisalMeeting.contract.changes.warning`)}
        </PerText>
      </PerFlexRow>
    )
  }

  const transformedChangeData = transformChangeData(data.contractChangesByEmployee)

  if (!!toggleExistingUnknownGovernmentJobTitle && !!governmentJobTitles && countrySettings.hasGovernmentJobTitleReferenceLevel) {
    let atLeastOneUnknownGjt = false
    for (const change of transformedChangeData) {
      const parsedChange: GovernmentJobTitleTinyDto | undefined = transformFromStringToGovernmentJobTitle(
        JSON.parse(change.changes).governmentJobTitle,
      )
      if (!!parsedChange && !isGovernmentJobTitleValid(governmentJobTitles, parsedChange)) {
        atLeastOneUnknownGjt = true
        toggleExistingUnknownGovernmentJobTitle(true)
        break
      }
    }
    if (!atLeastOneUnknownGjt) {
      toggleExistingUnknownGovernmentJobTitle(false)
    }
  } else if (!!toggleExistingUnknownGovernmentJobTitle) {
    toggleExistingUnknownGovernmentJobTitle(false)
  }

  return (
    <ContractChangeTable
      meeting={meeting}
      currentContract={currentContract}
      type="contractChange"
      tableData={transformedChangeData}
      careerPathEvaluation={careerPathEvaluation}
      careerPathData={careerPathData}
      isPreparation={isPreparation}
      countrySettings={countrySettings}
      governmentJobTitles={governmentJobTitles}
    />
  )
}
